import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'primereact/tooltip';
import { Nullable } from 'primereact/ts-helpers';
import { TableFilters } from '@/types/AccountStatementFilters';
import { Value } from '@/types/getPendingPaymentsType';

import { DropdownField, InputDateField } from '@/components';
import styles from './AccountStamentFilter.module.scss';

type AccountStatementFilterProps = {
  filters: TableFilters;
  setFilters: (filters: TableFilters) => void;
  setPaymentData: (data: Value | undefined) => void;
  selectionData: Value[];
  codigoOptions: { name: string; value: string }[];
};

const AccountStatementFilter: FC<AccountStatementFilterProps> = ({
  filters,
  setFilters,
  setPaymentData,
  selectionData = [],
  codigoOptions,
}) => {
  const { t } = useTranslation('accountStatement');

  const defaultVencimiento = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 2);
    return date;
  }, []);

  useEffect(() => {
    if (filters.vencimiento) {
      setFilters({
        ...filters,
        vencimiento: defaultVencimiento,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(() => {
    const res =
      selectionData?.map((item) => ({
        name: `${item?.numeroSeleccion}) ${item?.observaciones}`,
        value: item?.numeroSeleccion,
      })) || [];

    return [
      {
        name: `--- ${t('newSelection')} ---`,
        value: 0,
      },
      ...res,
    ];
  }, [selectionData, t]);

  const handleFilterChange = (
    value: string | Nullable<Date>,
    field: keyof TableFilters
  ) => {
    const newFilters = {
      ...filters,
      [field]: value,
    };

    setFilters(newFilters);
  };

  useEffect(() => {
    if (selectionData) {
      const dataParsed = selectionData?.map((item) => {
        const { listaDocumentosSeleccionados } = item;
        const total = listaDocumentosSeleccionados.reduce(
          (acum, { impMovMo }) => acum + (impMovMo || 0),
          0
        );

        const body = {
          ...item,
          totalAmount: total,
        };

        return body;
      });

      const newData = dataParsed.find(
        (val) => val.numeroSeleccion === Number(filters.selection)
      );

      setPaymentData(newData);
    }
  }, [selectionData, filters.selection, setPaymentData]);

  return (
    <div className="lg:w-2/3 w-full flex lg:flex-row flex-column">
      <DropdownField
        className="w-full lg:w-20rem lg:w-1/5x"
        id="codigo"
        name="Client"
        multiple
        value={filters.codigo}
        onChange={(e) => handleFilterChange(e.value, 'codigo')}
        options={codigoOptions}
        label={t('client')}
      />
      <InputDateField
        id="venciDesde"
        name="venciDesde"
        value={filters.vencimiento}
        label={t('defeated')}
        className={`${styles.dateField} lg:w-10rem lg:w-1/3 lg:ml-4 lg:mt-0 mt-2 h-full`}
        readOnlyInput
        onChange={(e) => handleFilterChange(e, 'vencimiento')}
      />
      <Tooltip position="top" />

      <DropdownField
        tooltip={t('tooltip')}
        className="w-full lg:w-20rem lg:w-1/5 lg:ml-4 lg:mt-0 mt-2"
        id="selection"
        name="Selección"
        value={filters?.selection}
        onChange={(e) => handleFilterChange(e.value, 'selection')}
        options={options}
        label={t('selection')}
      />
    </div>
  );
};

export default AccountStatementFilter;
